import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { useFinance } from "@/context/FinanceContext";
import { LOGIN } from "@/constants/testIds";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app/beranda";
  const { login } = useAuth();
  const { report } = useApp();
  const { setupCompleted } = useFinance();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await login(email.trim(), password);
      toast.success("Berhasil masuk");
      const explicitNext = params.get("next");
      const isAdminRole = d?.user?.role === "admin" || d?.user?.role === "super_admin";
      const dest = explicitNext || (isAdminRole ? "/admin" : "/app/beranda");
      navigate(dest);
    } catch (err) {
      toast.error(err.message || "Email atau password salah");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-lg bg-white px-6 pt-6" data-testid="login-page">
      <header className="mb-8 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-zinc-500" data-testid="login-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <img src={logo} alt="CeKKeU" data-testid="auth-logo" className="h-14 w-auto" />
      </header>

      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-zinc-900">Masuk</h1>
      <p className="mt-2 text-sm text-zinc-500">Buka laporan ketahanan keluarga Anda dari perangkat mana pun.</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-700 font-heading">Email</Label>
          <Input
            data-testid={LOGIN.emailInput}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nama@email.com"
            className="h-12 rounded-2xl border-zinc-200 text-base"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-zinc-700 font-heading">Password</Label>
          <Input
            data-testid={LOGIN.passwordInput}
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="h-12 rounded-2xl border-zinc-200 text-base"
          />
        </div>

        <div className="flex justify-end">
          <Link
            to="/lupa-password"
            data-testid={LOGIN.forgotPasswordLink}
            className="text-xs font-semibold text-emerald-600"
          >
            Lupa password?
          </Link>
        </div>

        <Button
          data-testid={LOGIN.submitButton}
          type="submit"
          disabled={busy}
          className="h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Masuk"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-zinc-500">
        Belum punya akun?{" "}
        <Link
          to={`/register?next=${encodeURIComponent(next)}`}
          data-testid={LOGIN.registerLink}
          className="font-semibold text-emerald-600"
        >
          Daftar
        </Link>
      </p>
    </div>
  );
}
